import { useState } from 'react';
import { Button } from '../ui/button';
import { OUTCOME_GROUPS, outcomeSetByText } from '../../lib/leads/outcomeGroups';
import type { LeadOutcome } from '../../types/leadOutcome.js';

// THE SHARED OUTCOME PICKER (Speed to Lead Stage 4.5C): the eight classifications plus "Not classified yet", grouped by
// stage, used by Kyck AND the club's portal on the lead list and on a call. The groups come from the ONE outcome table.
//
// ⚠️ An AI or system suggestion is PRE-SELECTED but never saved by this component: the button is labelled "Confirm" while
// the outcome is only a suggestion, and nothing happens until it is pressed. ⚠️ It makes no request on render (no effect,
// no query): the host passes `onSave`, and every save is a click.

export interface LeadOutcomePickerProps {
  id: string;
  current: LeadOutcome;
  /** A suggestion not yet on the lead (a call summary's), pre-selected when the lead is still unclassified. */
  suggested?: LeadOutcome | null;
  setBy?: 'person' | 'ai' | 'system' | null;
  onSave: (code: LeadOutcome) => Promise<void>;
  disabled?: boolean;
  /** Replaces the default note ("Suggested by AI: confirm or change it") with a more specific one: where the suggestion came from. */
  sourceNote?: string | null;
}

export function LeadOutcomePicker({ id, current, suggested = null, setBy = null, onSave, disabled = false, sourceNote = null }: LeadOutcomePickerProps) {
  const pending = current === 'unresolved' ? suggested : null;
  const [choice, setChoice] = useState<LeadOutcome | ''>(pending ?? (current === 'unresolved' ? '' : current));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSuggestion = pending !== null || setBy === 'ai';
  const unchanged = choice === current && !isSuggestion;
  const note = sourceNote ?? (pending ? 'Suggested by AI: confirm or change it' : outcomeSetByText(setBy));

  const save = async () => {
    if (choice === '') return;
    setBusy(true);
    setError(null);
    try {
      await onSave(choice);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the outcome.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="lead-outcome-picker">
      <select
        id={id}
        aria-label="Outcome"
        className="rounded border border-border bg-background px-2 py-1 text-sm"
        value={choice}
        disabled={disabled || busy}
        onChange={(e) => setChoice(e.target.value as LeadOutcome | '')}
      >
        <option value="">Choose…</option>
        {OUTCOME_GROUPS.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.outcomes.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </optgroup>
        ))}
      </select>
      <Button type="button" size="sm" className="kc-save" onClick={() => void save()} disabled={disabled || busy || choice === '' || unchanged}>
        {busy ? 'Saving…' : isSuggestion ? 'Confirm' : 'Save'}
      </Button>
      {note && <span className="text-xs text-muted-foreground" data-testid="lead-outcome-note">{note}</span>}
      {error && <span role="alert" className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
